import React, { lazy, Suspense, useState } from 'react';
import { object } from 'prop-types';
import { noop } from 'lodash';
import { shouldDisplayMigrationPopup } from '@fiverr-private/business_blocks';
import { getContext } from '@fiverr-private/fiverr_context';
import { isLoggedInUser } from '../../../utils/isLoggedInUser';
import { VARIANTS } from '../../../types/variants';
import { onGuestClick } from '../../../utils/contactSeller';
import { triggerFibMigrationPopup } from '../../../utils/fibMigrationPopup';
import { useOpenUserActivationModalWrapper } from '../UserActivationModalWrapper/utils';
import UserActivationModalWrapper from '../UserActivationModalWrapper';
import { SUPPORTED_ACTIONS } from '../UserActivationModalWrapper/constants';
import {
    reportCustomOrderClick,
    reportCustomOrderClose,
    reportCustomOrderSubmitSuccess
} from './utils';
import { getFormData } from './api';
import {
    allocateToBriefsExperiment,
    redirectToCreateBriefPage
} from './utils/briefs';
import { CUSTOM_ORDER_TRIGGER } from './constants';

const CUSTOM_ORDER_SOURCE = 'from_gig_page';

const CustomOrderModal = lazy(() =>
    import(/* webpackChunkName: 'CustomOrderModal' */'@fiverr-private/custom_order/src/entries/CustomOrderModalEntry')
);

const CustomOrder = (
    {
        children,
        bqSourceName
    },
    {
        pathfinderWrapper,
        biEvents,
        currencyConverter,
        logger,
        currentUser
    }) => {
    const [formData, setFormData] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [openModal, setOpenModal] = useState(() => noop);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const { shouldRenderAndOpenUserActivationModal } = useOpenUserActivationModalWrapper();
    const [renderAndOpenUserActivationModal, setRenderAndOpenUserActivationModal] = useState(false);

    const uploadOptions = {
        objectName: 'message',
        getPolicyUrl: pathfinderWrapper.pathfinder('messages_attachments_policy'),
        createPendingAttachmentIdUrl: pathfinderWrapper.pathfinder('messages_create_pending_attachment')
    };

    const isGuest = !isLoggedInUser();
    const biEnrichmentData = biEvents.getEnrichmentData();

    const onClick = async() => {
        reportCustomOrderClick(biEvents, bqSourceName);

        if (isGuest) {
            return onGuestClick({ biEvents, pathfinderWrapper });
        }

        if (await shouldRenderAndOpenUserActivationModal(currentUser)) {
            setRenderAndOpenUserActivationModal(true);
            return;
        }

        if (shouldDisplayMigrationPopup()){
            triggerFibMigrationPopup(CUSTOM_ORDER_TRIGGER, sendCustomOrder);
            return;
        }

        sendCustomOrder();
    };

    const sendCustomOrder = async() => {
        const briefsVariation = await allocateToBriefsExperiment(pathfinderWrapper, logger);

        if ([VARIANTS.B, VARIANTS.C].includes(briefsVariation)) {
            redirectToCreateBriefPage(pathfinderWrapper);
            return;
        }

        if (isValidFormData()) {
            openModal();
            return;
        }

        setIsLoading(true);

        try {
            const { data } = await getFormData({ pathfinderWrapper });
            setIsLoading(false);
            setFormData(data);
        } catch {
            setIsLoading(false);
        }
    };

    const setOpenModalHandle = (open) => {
        setOpenModal(() => open);
    };

    const isValidFormData = () => {
        const { seller, categoriesData } = formData;

        return !!(seller && categoriesData);
    };

    const handleClose = () => {
        if (!isSubmitted) {
            reportCustomOrderClose(biEvents);
        }
    };

    const handleSubmitSuccess = () => {
        setIsSubmitted(true);
        reportCustomOrderSubmitSuccess(biEvents);
    };

    const { pathParameters } = getContext();
    const { slug } = pathParameters;
    const { currency } = currencyConverter;
    const { seller, categoriesData } = formData;
    const submitPath = pathfinderWrapper.pathfinder('create_custom_order_request');
    const childProps = { onClick, disabled: isLoading };

    return (
        <div className="custom-order">
            {
                React.Children.map(children, (child) => {
                    if (child !== null) {
                        return React.cloneElement(child, childProps);
                    }
                })
            }
            {isValidFormData() &&
            <Suspense fallback={<div/>}>
                <CustomOrderModal gigSlug={slug}
                    source={CUSTOM_ORDER_SOURCE}
                    autoOpen={true}
                    trigger={setOpenModalHandle}
                    currency={currency}
                    seller={seller}
                    categoriesData={categoriesData}
                    submitPath={submitPath}
                    uploadOptions={uploadOptions}
                    biEnrichmentData={biEnrichmentData}
                    onSubmitSuccess={handleSubmitSuccess}
                    onClose={handleClose}/>
            </Suspense>
            }
            <UserActivationModalWrapper renderAndOpen={renderAndOpenUserActivationModal}
                onClose={() => setRenderAndOpenUserActivationModal(false)}
                action={SUPPORTED_ACTIONS.GET_A_QUOTE}/>
        </div>
    );
};

CustomOrder.propTypes = {
    children: object
};

CustomOrder.contextTypes = {
    biEvents: object,
    pathfinderWrapper: object,
    currencyConverter: object,
    logger: object,
    currentUser: object
};

export default CustomOrder;
